/**
 * useVenue — React Query hooks for the venue detail page (BON-159).
 *
 * `useVenue` loads the full venue record (with audit) by id;
 * `useUpdateVenue` PATCHes a partial change set and writes the fresh venue
 * back into the cache so the page re-renders from the server's view (the
 * source of truth for derived fields like `isActive`).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  deactivateVenue,
  fetchVenueById,
  updateVenue,
  type VenueDetail,
  type VenuePatch,
} from '../../../api/venues';

import { ROSTER_KEY } from './roster/venues-roster-api';
import { VENUE_TASK_COUNT_KEY } from './use-venue-task-count';

const STALE_30S = 30_000;

export const venueKey = (id: string) => ['venue', id] as const;

export function useVenue(id: string) {
  return useQuery({
    queryKey: venueKey(id),
    queryFn: () => fetchVenueById(id),
    enabled: id.length > 0,
    staleTime: STALE_30S,
  });
}

/** The lists and counts that a venue's region or active state feeds. A venue that
 *  moves region or is deactivated changes whose task counts it belongs to. */
function refreshVenueLists(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['dashboards.admin', 'venues'] });
  void qc.invalidateQueries({ queryKey: ['dashboards.regional'] });
  void qc.invalidateQueries({ queryKey: ROSTER_KEY });
  void qc.invalidateQueries({ queryKey: VENUE_TASK_COUNT_KEY });
}

/** Puts a fresh venue from the server into this venue's cache and refreshes the venue lists. */
export function useApplyVenue(id: string) {
  const qc = useQueryClient();
  return (venue: VenueDetail) => {
    qc.setQueryData(venueKey(id), venue);
    refreshVenueLists(qc);
  };
}

/** Fetches this venue again, for when a change went through but the reply could not be read. */
export function useRefreshVenue(id: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: venueKey(id) });
}

/** PATCH a venue; refreshes this venue's cache and the venue lists. */
export function useUpdateVenue(id: string) {
  const applyVenue = useApplyVenue(id);
  return useMutation({
    mutationFn: (patch: VenuePatch) => updateVenue(id, patch),
    onSuccess: (result) => applyVenue(result.venue),
  });
}

/** Deactivate a venue; refreshes the lists and counts it was part of. */
export function useDeactivateVenue(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deactivateVenue(id),
    onSuccess: () => refreshVenueLists(qc),
  });
}
